import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { normalizeQuranAnswer } from './quranQuiz';
import './QuranPage.css';
import './QuranRefresh.css';
import './QuranExperience.css';

class QuizModalErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('Quran quiz modal crashed:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, textAlign: 'center', direction: 'rtl' }}>
          <p style={{ color: '#e74c3c', fontWeight: 700, marginBottom: 8 }}>
            حدث خطأ أثناء عرض الاختبار
          </p>
          <p style={{ fontSize: 12, color: '#9ca3af', wordBreak: 'break-word' }}>
            {String(this.state.error.message || this.state.error)}
          </p>
          <button className="quran-quiz-btn-primary" style={{ marginTop: 16 }} onClick={this.props.onClose}>
            إغلاق
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function QuranQuizDialog({ quizModal, setQuizModal, theme, isArabic, onClose }) {
  const dialogRef = useRef(null), closeRef = useRef(onClose);
  closeRef.current = onClose;
  const closeQuiz = onClose;
  const isAnswerCorrect = (answer, expected) => normalizeQuranAnswer(answer) === normalizeQuranAnswer(expected);
  useEffect(() => {
    const previous = document.activeElement, overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('button')?.focus();
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const buttons = [...(dialogRef.current?.querySelectorAll('button:not(:disabled),[tabindex="0"]') || [])].filter(node => node.getClientRects().length);
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keydown); if (previous?.isConnected) previous.focus({ preventScroll: true }); };
  }, []);
  // يُستخدم لأسئلة: كتابة / اختيارات / الآية التالية
  const submitQuizAnswer = (chosenText) => {
    setQuizModal(prev => {
      if (!prev || prev.answered) return prev;
      const current = prev.questions[prev.index];
      const correct = isAnswerCorrect(chosenText, current.answer);
      return {
        ...prev,
        answered: true,
        wasCorrect: correct,
        chosenAnswer: chosenText,
        correctCount: prev.correctCount + (correct ? 1 : 0),
      };
    });
  };

  // سؤال الترتيب: الضغط على آية يضيفها للتسلسل، والضغط عليها مجدداً *يلغي* اختيارها (تراجع)
  const tapOrderItem = (ayahNumber) => {
    setQuizModal(prev => {
      if (!prev || prev.answered) return prev;
      const current = prev.questions[prev.index];
      if (prev.orderSelection.includes(ayahNumber)) {
        // تراجع: إزالة هذه الآية من التسلسل المختار
        return { ...prev, orderSelection: prev.orderSelection.filter(n => n !== ayahNumber) };
      }
      const nextSelection = [...prev.orderSelection, ayahNumber];
      if (nextSelection.length < current.correctOrder.length) {
        return { ...prev, orderSelection: nextSelection };
      }
      // اكتمل الاختيار — تحقق من الترتيب
      const correct = nextSelection.every((n, i) => n === current.correctOrder[i]);
      return {
        ...prev,
        orderSelection: nextSelection,
        answered: true,
        wasCorrect: correct,
        correctCount: prev.correctCount + (correct ? 1 : 0),
      };
    });
  };

  // زر "تراجع" صريح لمسح آخر اختيار في سؤال الترتيب
  const undoLastOrderPick = () => {
    setQuizModal(prev => {
      if (!prev || prev.answered || prev.orderSelection.length === 0) return prev;
      return { ...prev, orderSelection: prev.orderSelection.slice(0, -1) };
    });
  };

  // ── سؤال السحب والإفلات ───────────────────────────────────────────────────
  // اختيار/تحديد كلمة من بنك الكلمات (نقرة بديلة تعمل باللمس وبالحاسوب أيضاً)
  const selectDragWord = (wordId) => {
    setQuizModal(prev => {
      if (!prev || prev.answered) return prev;
      return { ...prev, dragSelectedWord: prev.dragSelectedWord === wordId ? null : wordId };
    });
  };

  // وضع كلمة داخل فراغ معيّن — إمّا الكلمة المحددة حالياً أو كلمة مُمرّرة (سحب فعلي بالماوس)
  const placeWordInSlot = (idx, wordIdOverride) => {
    setQuizModal(prev => {
      if (!prev || prev.answered) return prev;
      const current = prev.questions[prev.index];
      if (!current || current.qType !== 'dragdrop') return prev;
      const wordId = wordIdOverride || prev.dragSelectedWord;
      if (!wordId) return prev;
      const placements = { ...prev.dragPlacements };
      // إزالة هذه الكلمة من أي فراغ آخر كانت موضوعة فيه
      Object.keys(placements).forEach(k => { if (placements[k] === wordId) delete placements[k]; });
      placements[idx] = wordId;
      return { ...prev, dragPlacements: placements, dragSelectedWord: null };
    });
  };

  const removeWordFromSlot = (idx) => {
    setQuizModal(prev => {
      if (!prev || prev.answered) return prev;
      const placements = { ...prev.dragPlacements };
      delete placements[idx];
      return { ...prev, dragPlacements: placements };
    });
  };

  // سحب حقيقي بالماوس (Desktop) عبر HTML5 Drag & Drop API
  const handleChipDragStart = (wordId) => (e) => {
    e.dataTransfer.setData('text/plain', wordId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSlotDragOver = (e) => {
    e.preventDefault(); // ضروري للسماح بالإفلات
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSlotDrop = (idx) => (e) => {
    e.preventDefault();
    const wordId = e.dataTransfer.getData('text/plain');
    if (wordId) placeWordInSlot(idx, wordId);
  };

  const submitDragDropAnswer = () => {
    setQuizModal(prev => {
      if (!prev || prev.answered) return prev;
      const current = prev.questions[prev.index];
      if (!current || current.qType !== 'dragdrop') return prev;
      const correct = current.slots.every(slot => {
        const placedWordId = prev.dragPlacements[slot.idx];
        const placedWord = current.bank.find(b => b.wordId === placedWordId);
        return placedWord && isAnswerCorrect(placedWord.word, slot.correctWord);
      });
      return {
        ...prev,
        answered: true,
        wasCorrect: correct,
        correctCount: prev.correctCount + (correct ? 1 : 0),
      };
    });
  };

  const nextQuizQuestion = () => {
    setQuizModal(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        index: prev.index + 1,
        answered: false,
        orderSelection: [],
        dragPlacements: {},
        dragSelectedWord: null,
        wasCorrect: null,
      };
    });
  };


  return createPortal(
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="تدريب القرآن الكريم" className={`quran-quiz-fullscreen ${theme === 'dark' ? 'theme-dark-item' : ''}`}>
          <QuizModalErrorBoundary onClose={closeQuiz}>
            <div className="quran-quiz-header">
              <button aria-label={isArabic?'إغلاق التدريب':'Close practice'} className="quran-quiz-close" onClick={closeQuiz}><X size={22} /></button>
              <h3>{quizModal.title || `اختبار صفحة ${quizModal.pageNumber?.toLocaleString?.('ar') ?? quizModal.pageNumber}`}</h3>
              <div style={{ width: 22 }} />
            </div>

            {!quizModal.loading && quizModal.questions.length > 0 && quizModal.index < quizModal.questions.length && (
              <div className="quran-quiz-topbar">
                <div className="quran-quiz-progress-track">
                  <div
                    className="quran-quiz-progress-fill"
                    style={{ width: `${(quizModal.index / quizModal.questions.length) * 100}%` }}
                  />
                </div>
                <div className="quran-quiz-progress-label">
                  سؤال {quizModal.index + 1} من {quizModal.questions.length}
                </div>
              </div>
            )}

            <div className="quran-quiz-body">
              {quizModal.practiceOnly && quizModal.index < quizModal.questions.length && <p className="quran-practice-only">{isArabic ? 'تدريب من النص المحمّل · دون نقاط' : 'Practice from downloaded text · no points'}</p>}
              {quizModal.loading ? (
                <LoadingSpinner kind="quiz" />
              ) : quizModal.questions.length === 0 ? (
                <p className="quran-quiz-empty">تعذّر تحضير أسئلة لهذه الصفحة.</p>
              ) : quizModal.index >= quizModal.questions.length ? (
                // ── شاشة النتيجة ───────────────────────────────────────────────
                (() => {
                  const percentage = Math.round((quizModal.correctCount / quizModal.questions.length) * 100);
                  const passed = percentage >= 95;
                  return (
                    <div className="quran-quiz-summary">
                      <div className="quran-quiz-summary-emoji">
                        {passed ? '🏆' : percentage >= 50 ? '🎯' : '📖'}
                      </div>
                      <div className="quran-quiz-summary-score">
                        {quizModal.correctCount} / {quizModal.questions.length}
                      </div>
                      <p>إجابات صحيحة ({percentage}%)</p>
                      {quizModal.practiceOnly ? (
                        <p className="quran-quiz-award-note">{isArabic ? 'تدريب دون اتصال · لا تُمنح نقاط ولا تُرسل النتيجة لاحقًا.' : 'Offline practice · no points or later score submission.'}</p>
                      ) : quizModal.recording ? (
                        <p className="quran-quiz-award-note">جاري حفظ النتيجة…</p>
                      ) : quizModal.recordError ? (
                        <p className="quran-quiz-award-note warn">تعذّر حفظ النتيجة. تحقق من الاتصال وحاول مرة أخرى.</p>
                      ) : passed ? (
                        quizModal.achievementAwarded ? (
                          <p className="quran-quiz-award-note success">🎉 مبارك! حصلت على نقطة إنجاز لصفحة القرآن الكريم.</p>
                        ) : (
                          <p className="quran-quiz-award-note">✅ نجحت! سبق أن حصلت على نقطة إنجاز هذه الصفحة.</p>
                        )
                      ) : (
                        <p className="quran-quiz-award-note warn">تحتاج 95% فأكثر لكسب نقطة إنجاز هذه الصفحة — حاول مرة أخرى.</p>
                      )}
                      <button className="quran-quiz-btn-primary" onClick={closeQuiz}>إغلاق</button>
                    </div>
                  );
                })()
              ) : (
                // ── Question screen — branches per question type ─────────────────
                (() => {
                  const q = quizModal.questions[quizModal.index];

                  if (q.qType === 'mcq') {
                    return (
                      <div className="quran-quiz-question">
                        <div className="quran-quiz-type-badge">{q.blankCount>1?`أكمل الآية بـ ${q.blankCount} كلمات بالترتيب`:'أكمل الآية من الخيارات'}</div>

                        <p className="quran-quiz-blanked-text">{q.blankedText}</p>

                        <div className="quran-quiz-options">
                          {q.options.map((opt, i) => {
                            const isCorrectOpt = isAnswerCorrect(opt, q.answer);
                            const showState = quizModal.answered;
                            let cls = 'quran-quiz-option';
                            if (showState && isCorrectOpt) cls += ' correct';
                            else if (showState && opt===quizModal.chosenAnswer) cls += ' wrong';
                            else if (showState) cls += ' dim';
                            return (
                              <button key={i} className={cls} disabled={quizModal.answered} onClick={() => submitQuizAnswer(opt)}>
                                {opt}
                              </button>
                            );
                          })}
                        </div>

                        {quizModal.answered && (
                          <div className={`quran-quiz-feedback ${quizModal.wasCorrect ? 'correct' : 'wrong'}`}>
                            {quizModal.wasCorrect ? '✅ إجابة صحيحة' : `❌ الإجابة الصحيحة: ${q.answer}`}
                            <p className="quran-quiz-full-ayah">{q.fullText}</p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (q.qType === 'next') {
                    return (
                      <div className="quran-quiz-question">
                        <div className="quran-quiz-type-badge">➡️ ما هي الآية التالية؟</div>

                        <p className="quran-quiz-prompt-ayah">
                          <span className="quran-quiz-ayah-num">{q.promptAyahNumber}</span> {q.promptText}
                        </p>

                        <div className="quran-quiz-options quran-quiz-options-stacked">
                          {q.options.map((opt, i) => {
                            const isCorrectOpt = isAnswerCorrect(opt, q.answer);
                            const showState = quizModal.answered;
                            let cls = 'quran-quiz-option quran-quiz-option-long';
                            if (showState && isCorrectOpt) cls += ' correct';
                            else if (showState && opt===quizModal.chosenAnswer) cls += ' wrong';
                            else if (showState) cls += ' dim';
                            return (
                              <button key={i} className={cls} disabled={quizModal.answered} onClick={() => submitQuizAnswer(opt)}>
                                {opt}
                              </button>
                            );
                          })}
                        </div>

                        {quizModal.answered && (
                          <div className={`quran-quiz-feedback ${quizModal.wasCorrect ? 'correct' : 'wrong'}`}>
                            {quizModal.wasCorrect ? '✅ إجابة صحيحة' : '❌ إجابة خاطئة — الآية الصحيحة:'}
                            <p className="quran-quiz-full-ayah">
                              <span className="quran-quiz-ayah-num">{q.answerAyahNumber}</span> {q.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (q.qType === 'order') {
                  const selection = quizModal.orderSelection;
                  return (
                    <div className="quran-quiz-question">
                      <div className="quran-quiz-type-badge">🔢 رتّب الآيات بالترتيب الصحيح</div>
                      <p className="quran-quiz-order-hint">اضغط على الآيات بالترتيب الصحيح</p>

                      <div className="quran-quiz-order-list">
                        {q.items.map((item) => {
                          const selIdx = selection.indexOf(item.number);
                          const isSelected = selIdx !== -1;
                          const isCorrectPos = quizModal.answered && q.correctOrder[selIdx] === item.number;
                          let cls = 'quran-quiz-order-item';
                          if (isSelected) cls += ' selected';
                          if (quizModal.answered) cls += isCorrectPos ? ' correct' : ' wrong';
                          return (
                            <button
                              key={item.number}
                              className={cls}
                              disabled={quizModal.answered}
                              onClick={() => tapOrderItem(item.number)}
                              title={isSelected ? 'اضغط مجدداً للتراجع عن هذا الاختيار' : ''}
                            >
                              {isSelected && <span className="quran-quiz-order-badge">{selIdx + 1}</span>}
                              <span className="quran-quiz-order-text">{item.text}</span>
                            </button>
                          );
                        })}
                      </div>

                      {!quizModal.answered && selection.length > 0 && (
                        <button className="quran-quiz-undo-btn" onClick={undoLastOrderPick}>
                          ↩️ تراجع عن آخر اختيار
                        </button>
                      )}

                      {quizModal.answered && (
                        <div className={`quran-quiz-feedback ${quizModal.wasCorrect ? 'correct' : 'wrong'}`}>
                          {quizModal.wasCorrect ? '✅ ترتيب صحيح' : '❌ الترتيب الصحيح مرقّم أعلاه بالأخضر'}
                        </div>
                      )}
                    </div>
                  );
                }

                // qType === 'dragdrop' — سحب الكلمات ووضعها في الفراغات الصحيحة
                return (
                  <div className="quran-quiz-question">
                    <div className="quran-quiz-type-badge">🧩 اسحب الكلمات وضعها في مكانها الصحيح</div>
                    <p className="quran-quiz-order-hint">اسحب الكلمة بالماوس وأفلتها في الفراغ — أو اضغط على الكلمة ثم اضغط على الفراغ (يعمل باللمس على الهاتف)</p>

                    <p className="quran-quiz-dragdrop-sentence" dir="rtl">
                      {q.words.map((w, i) => {
                        const slot = q.slots.find(s => s.idx === i);
                        if (!slot) {
                          return <span key={i} className="quran-dd-word">{w} </span>;
                        }
                        const placedWordId = quizModal.dragPlacements[slot.idx];
                        const placedWord = q.bank.find(b => b.wordId === placedWordId);
                        const showState = quizModal.answered;
                        const isCorrectSlot = showState && placedWord && isAnswerCorrect(placedWord.word, slot.correctWord);
                        let slotCls = 'quran-dd-slot';
                        if (placedWord) slotCls += ' filled';
                        if (showState) slotCls += isCorrectSlot ? ' correct' : ' wrong';
                        return (
                          <span
                            key={i}
                            className={slotCls}
                            onDragOver={handleSlotDragOver}
                            onDrop={handleSlotDrop(slot.idx)}
                            onClick={() => {
                              if (quizModal.answered) return;
                              if (quizModal.dragSelectedWord) placeWordInSlot(slot.idx);
                              else if (placedWord) removeWordFromSlot(slot.idx);
                            }}
                          >
                            {placedWord ? placedWord.word : '＿＿＿'}
                          </span>
                        );
                      })}
                    </p>

                    <div className="quran-dd-bank">
                      {q.bank.map((b) => {
                        const isUsed = Object.values(quizModal.dragPlacements).includes(b.wordId);
                        const isSelected = quizModal.dragSelectedWord === b.wordId;
                        let chipCls = 'quran-dd-chip';
                        if (isUsed) chipCls += ' used';
                        if (isSelected) chipCls += ' selected';
                        return (
                          <button
                            key={b.wordId}
                            className={chipCls}
                            disabled={quizModal.answered || isUsed}
                            draggable={!quizModal.answered && !isUsed}
                            onDragStart={handleChipDragStart(b.wordId)}
                            onClick={() => selectDragWord(b.wordId)}
                          >
                            {b.word}
                          </button>
                        );
                      })}
                    </div>

                    {!quizModal.answered && (
                      <button
                        className="quran-quiz-btn-primary"
                        disabled={Object.keys(quizModal.dragPlacements).length < q.slots.length}
                        onClick={submitDragDropAnswer}
                      >تحقق</button>
                    )}

                    {quizModal.answered && (
                      <div className={`quran-quiz-feedback ${quizModal.wasCorrect ? 'correct' : 'wrong'}`}>
                        {quizModal.wasCorrect ? '✅ إجابة صحيحة' : '❌ إجابة خاطئة'}
                        <p className="quran-quiz-full-ayah">{q.fullText}</p>
                      </div>
                    )}
                  </div>
                );
              })()
              )}
            </div>

            {!quizModal.loading && quizModal.questions.length > 0 && quizModal.index < quizModal.questions.length && quizModal.answered && (
              <div className="quran-quiz-footer">
                <button className="quran-quiz-btn-primary" onClick={nextQuizQuestion}>
                  {quizModal.index + 1 >= quizModal.questions.length ? 'عرض النتيجة' : 'السؤال التالي'}
                </button>
              </div>
            )}
          </QuizModalErrorBoundary>
        </div>, document.body);
}
